import React, { useRef, useState, useCallback, useEffect } from "react";
import { MoveLeft } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { Dropdown, Button, Spinner, ToastContainer, Toast, OverlayTrigger, Tooltip } from "react-bootstrap";
import DOMPurify from "dompurify";
import ErrorExtractor from "../common/error_extractor";
import CommonDropdown from "../common/common_dropDown";
import CommonTooltip from "../common/common_tooltip";
import { ReactComponent as InfoIc } from "../../assets/svg/info_ic.svg";
import { submitPOAPI, updatePOAPI, getPOEditApi, getProductDetailsApi, submitCategoryApi } from "./po-api";
import addImage from "../../assets/svg/add-image.svg";
import { X } from "lucide-react"; // Close icon
import upload from "../../assets/svg/upload.svg";
import attachment from "../../assets/png/attachment.png";
import download from "../../assets/svg/download.svg";
import closeAttachment from "../../assets/svg/close-red.svg";
import { Sidebar } from "../common/Sidebar";
import { Navbar } from "../common/Navbar";
import CommonDatePicker from "../common/common_datepicker";
import { Calendar } from "lucide-react";
import deleteIcon from "../../assets/svg/delete-po.svg";
import "./poCreate.css";
import SearchWithFreeText from "../common/common_auto_freesuggestion";
import { ReactComponent as AddIc } from "../../assets/svg/add_icon.svg";
import { getVendorAddress } from "../purchase-quotation/pq-api";
import { FaTimes } from "react-icons/fa";
import fileUploadIcon from "../../assets/svg/file_upload.svg";
import ProfileImageModal from "../common/common-preview";
import AmountSummary from "../common/AmountSummary";

export const PurchaseOrderCreate = () => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const adminUrl = process.env.REACT_APP_ADMIN_API_URL;
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [imageRemoved, setImageRemoved] = useState(false);
  const [viewFiles, setViewFiles] = useState([]);
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = location.state || {};
  const vendorDetails = location.state;
  const from = location.state?.from;

  const decodeHtml = (html) => {
    const txt = document.createElement("textarea");
    txt.innerHTML = html;
    return txt.value;
  };

  const [showProfilePopup, setShowProfilePopup] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState("");

  const handleFileRemove = (fileId) => {
    setUploadedFiles((prev) => prev.filter((file) => file.id !== fileId));
  };
  const handleSelectFile = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*,";
    input.multiple = true;
    input.onchange = (e) => {
      const files = Array.from(e.target.files);
      const newFiles = files.map((file, index) => {
        const sizeKB = (file.size / 1024).toFixed(2);
        const isImage = file.type.startsWith("image/");
        let preview = null;

        if (isImage) {
          preview = URL.createObjectURL(file);
        }

        return {
          id: Date.now() + index,
          name: file.name,
          size: `${sizeKB} KB`,
          type: file.type,
          preview: preview,
          file: file, // Store actual file object for upload
        };
      });
      setUploadedFiles((prev) => [...prev, ...newFiles]);
    };
    input.click();
  };

  const openFile = (fileUrl = "", fileName = "") => {
    // prefer filename extension (CREATE flow)
    const source = (fileName || fileUrl).toLowerCase();
    const cleanSource = source.split("?")[0];

    // 🖼 Images → popup
    if (cleanSource.endsWith(".png") || cleanSource.endsWith(".jpg") || cleanSource.endsWith(".jpeg") || cleanSource.endsWith(".gif") || cleanSource.endsWith(".webp")) {
      if (!fileUrl) return; // no preview without URL
      setSelectedProfile(fileUrl);
      setShowProfilePopup(true);
      return;
    }

    // 📄 PDF → open directly
    if (cleanSource.endsWith(".pdf")) {
      if (!fileUrl) return;
      setSelectedProfile(fileUrl);
      setShowProfilePopup(true);
      return;
    }

    // 📊 Office files → Office Viewer
    if (cleanSource.endsWith(".xls") || cleanSource.endsWith(".xlsx") || cleanSource.endsWith(".doc") || cleanSource.endsWith(".docx") || cleanSource.endsWith(".ppt") || cleanSource.endsWith(".pptx")) {
      if (!fileUrl) return;

      const officeViewerUrl = "https://view.officeapps.live.com/op/view.aspx?src=" + encodeURIComponent(fileUrl);

      window.open(officeViewerUrl, "_blank", "noopener,noreferrer");
      return;
    }

    // ⬇ Fallback
    if (fileUrl) {
      window.open(fileUrl, "_blank", "noopener,noreferrer");
    }
  };
  const handleBack = () => {
    if (from === "vendor") {
      navigate("/viewVendor", {
        state: {
          activeTab: "po",
          id: location.state?.vendorId,
        },
      });
    } else {
      navigate("/purchase-order");
    }
  };
  const [isLoader, setIsLoader] = useState(false);
  const [isCatLoader, setIsCatLoader] = useState(false);
  const [productList, setProductList] = useState([]);
  const [isRoundOff, setIsRoundOff] = useState(false);
  const [adjustment, setAdjustment] = useState("");

  const [removedProductList, setRemovedProductList] = useState([]);
  useEffect(() => {
    if (vendorDetails) {
      setVendorOption({ value: vendorDetails.vendorId, label: vendorDetails.vendorName });
    }
  }, [vendorDetails]);
  useEffect(() => {
    if (id != null) {
      getPurchaseOrderEdit(id);
    } else {
      setProductList([
        {
          id: 0,
          proceed: null,
          product: null,
          product_code: null,
          product_img: null,
          unit: null,
          date: null,
          quantity: 0,
          unit_without_tax: null,
          cgst_percentage: null,
          sgst_percentage: null,
          igst_percentage: null,
        },
      ]);
    }
  }, [id]);

  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarStatus, setSnackbarStatus] = useState(false);
  const handleSnackbarShow = () => setShowSnackbar(true);
  const handleSnackbarClose = () => setShowSnackbar(false);

  const [poNumber, setPoNumber] = useState("");
  const [projectOption, setProjectOption] = useState(null);
  const [paymentTypeOption, setPaymentTypeOption] = useState(null);
  const [vendorOption, setVendorOption] = useState(null);
  const [currencyOption, setCurrencyOption] = useState(null);
  const [description, setDescription] = useState("");
  const [deliveryDate, setDeliveryDate] = useState();
  const [poDate, setPODate] = useState();
  const [paymentTerms, setPaymentTerms] = useState("");
  const [incoTerms, setIncoTerms] = useState("");
  const [termConditions, setTermConditions] = useState("");
  const [noteInstructions, setNoteInstructions] = useState("");

  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchCategory, setSearchCategory] = useState("");
  const [createCategoryModal, setCreateCategoryModal] = useState(false);
  const [categoryName, setCategoryName] = useState("");

  const handleDropdownCategoryChange = (option) => {
    console.log("Selected option:", option);
    setSelectedCategory(option);
    setSearchCategory(option.value ?? "");
  };

  const handleSearchCategoryChange = (value) => {
    console.log("Search input changed:", value);
    setSearchCategory(value);
    setSelectedCategory({ label: value, value: value });
  };

  const getProductDetails = async (id, index) => {
    try {
      const response = await getProductDetailsApi(id);

      if (response.data.success === true) {
        const productDetail = response.data.product ?? {};
        const productTemp = [...productList];
        productTemp[index]["product_code"] = productDetail.code ?? "";
        productTemp[index]["product_img"] = productDetail.image ?? "";
        setProductList(productTemp);
      } else {
      }
      return response.data;
    } catch (err) {
      console.error("Error fetching category:", err);
    } finally {
    }
  };

  const [errorMsg, setErrorMsg] = useState({
    vendor_id: "",
    project_id: "",
    currency_id: "",
    payment_type: "",
    description: "",
    category: "",
  });
  const [errorCatMsg, setErrorCatMsg] = useState({
    name: "",
  });
  const [groupedError, setGroupedError] = useState({});
  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
    });
  const submitForm = async () => {
    setIsLoader(true);
    const extractor = new ErrorExtractor();

    try {
      const updatedData = productList.map((item) => ({
        ...item,
        proceed: item.proceed != null ? item.proceed : false,
        product_id: item.product != null ? item.product.value : "",
        unit_id: item.unit != null ? item.unit.value : "",
        date: item.date != null && item.date !== "" ? item.date.toLocaleDateString("en-CA") : "",
      }));
      let base64File;
      if (uploadedFiles.length > 0) {
        base64File = await fileToBase64(uploadedFiles[0].file);
      }
      const preSanitizeData = {
        category_id: selectedCategory != null ? (selectedCategory?.value ?? "") : "",
        vendor_id: vendorOption != null ? vendorOption.value : "",
        project_id: projectOption != null ? projectOption.value : "",
        payment_type_id: paymentTypeOption != null ? paymentTypeOption.value : "",
        currency_id: currencyOption != null ? currencyOption.value : "",
        description: description,
        payment_terms: paymentTerms,
        po_number: poNumber,
        inco_terms: incoTerms,
        note_instruction: noteInstructions,
        terms_condition: termConditions,
        products: updatedData,
        removed_lines: removedProductList ?? [],
        image: base64File,
        delivery_date: deliveryDate != null && deliveryDate !== "" ? deliveryDate.toLocaleDateString("en-CA") : "",
        po_date: poDate != null && poDate !== "" ? poDate.toLocaleDateString("en-CA") : "",
        sign_removed: imageRemoved,
        round_off: isRoundOff,
        adjustment: Math.abs(toNumber(adjustment)),
        is_add: toNumber(adjustment) >= 0,
      };
      const sanitizedData = {};
      Object.entries(preSanitizeData).forEach(([key, value]) => {
        if (typeof value === "boolean" || typeof value === "number" || Array.isArray(value) || key === "assignee") {
          sanitizedData[key] = value;
        } else {
          sanitizedData[key] = DOMPurify.sanitize(value);
        }
      });
      var response = null;
      if (id == null) {
        response = await submitPOAPI(sanitizedData);
      } else {
        response = await updatePOAPI(id, sanitizedData);
      }

      console.log("sdddd", response);
      // Check if response indicates failure
      if (response.status === 400) {
        new Promise((resolve, reject) => {
          const groupedErrorTemp = {};
          Object.entries(response.data.error).forEach(([key, value]) => {
            // match tickets.index.field
            const match = key.match(/^products\.(\d+)\.(.+)$/);

            if (match) {
              const index = match[1]; // e.g. "0"
              const field = match[2]; // e.g. "closing_date"

              if (!groupedErrorTemp[index]) groupedErrorTemp[index] = {};
              groupedErrorTemp[index][field] = value;
            }
          });
          setGroupedError(groupedErrorTemp);

          const validationTemp = {
            trans_type: "",
            vendor_id: "",
            project_id: "",
            po_id: "",
            amount: "",
            payment_type: "",
            description: "",
            category: "",
          };
          Object.entries(response.data.error).forEach(([key, value]) => {
            console.log(`${key}: ${value}`);
            validationTemp[key] = value.length === 0 ? "" : value[0];
          });
          console.log("validationTemp...", validationTemp);
          setErrorMsg(validationTemp);
        });
      } else if (response.data.success === true) {
        setSnackbarMessage(response.data.message);
        setSnackbarStatus(response.data.success);
        handleSnackbarShow();

        handleBack();
      }
    } catch (err) {
      console.error("Error submitting form:", err);

      // Optional: handle network errors separately
      if (err.response && err.response.status === 400 && err.response.data.error) {
        new Promise((resolve, reject) => {
          const groupedErrorTemp = {};
          Object.entries(err.response.data.error).forEach(([key, value]) => {
            // match tickets.index.field
            const match = key.match(/^products\.(\d+)\.(.+)$/);

            if (match) {
              const index = match[1]; // e.g. "0"
              const field = match[2]; // e.g. "closing_date"

              if (!groupedErrorTemp[index]) groupedErrorTemp[index] = {};
              groupedErrorTemp[index][field] = value;
            }
          });
          setGroupedError(groupedErrorTemp);
        });
        const errorvalue = extractor.extractErrors(err.response.data, {
          ...errorMsg,
        });
        setErrorMsg(errorvalue);
      }
    } finally {
      setIsLoader(false);
    }
  };

  const getPurchaseOrderEdit = async (id) => {
    try {
      const response = await getPOEditApi(id);

      if (response.data.success === true) {
        const poDetail = response.data.order ?? {};
        getAddress(poDetail.vendor_id ?? "");
        setVendorOption({
          value: poDetail.vendor_id ?? "",
          label: poDetail.vendor_name ?? "",
        });
        setCurrencyOption({
          value: poDetail.currency_id === 0 ? "" : (poDetail.currency_id ?? ""),
          label: poDetail.currency_name ?? "",
          symbol: poDetail.currency_symbol ? decodeHtml(poDetail.currency_symbol) : "₹",
        });
        setPaymentTypeOption({
          value: poDetail.payment_type_id ?? "",
          label: poDetail.payment_type ?? "",
        });
        setProjectOption({
          value: poDetail.project_id === 0 ? "" : (poDetail.project_id ?? ""),
          label: poDetail.project_name ?? "",
        });
        setDescription(poDetail.description ?? "");
        setPaymentTerms(poDetail.payment_terms ?? "");
        setDeliveryDate(poDetail.delivery_date ? new Date(poDetail.delivery_date) : null);
        setPODate(poDetail.po_date ? new Date(poDetail.po_date) : null);
        setPoNumber(poDetail.po_number ?? "");
        setIncoTerms(poDetail.inco_terms ?? "");
        setNoteInstructions(poDetail.note_instruction ?? "");
        setTermConditions(poDetail.terms_condition ?? "");
        setSearchCategory(poDetail.category_name ?? "");
        console.log("rounddiff");
        console.log(Number(poDetail.round_diff) !== 0);
        setIsRoundOff(Number(poDetail.round_diff) !== 0);
        const adjValue = poDetail.adjustment ?? 0;
        setAdjustment(poDetail.is_add === false ? `-${adjValue}` : adjValue);
        setSelectedCategory({
          value: poDetail.category_id ?? "",
          label: poDetail.category_name ?? "",
        });
        if (poDetail.image) {
          setViewFiles([{ preview: poDetail.image }]);
        }

        const productTemp = [];
        poDetail.products.forEach((item, index) => {
          productTemp.push({
            id: item.id,
            product: { value: item.product_id, label: item.name },
            product_code: item.code,
            product_img: item.image,
            unit: { value: item.unit_id, label: item.unit_name },
            date: item.date != null && item.date !== "" ? new Date(item.date) : null,
            quantity: item.quantity,
            unit_without_tax: item.unit_without_tax,

            cgst_percentage: item.cgst_percentage,

            sgst_percentage: item.sgst_percentage,
            igst_percentage: item.igst_percentage,
          });
        });
        setProductList(productTemp);
      } else {
      }
      return response.data;
    } catch (err) {
      console.error("Error fetching category:", err);
    } finally {
    }
  };

  const getErrorMsg = (index, field) => {
    if (field === "product_id") {
      if (groupedError[index] != null) {
        if (groupedError[index].product_id != null) {
          return groupedError[index].product_id[0]?.replace(`products.${index}.product_id`, "product") ?? "";
        }
      }
    }
    if (field === "exception") {
      if (groupedError[index] != null) {
        if (groupedError[index].exception != null) {
          return groupedError[index].exception[0]?.replace(`products.${index}.exception`, "exception") ?? "";
        }
      }
    }
    if (field === "unit_id") {
      if (groupedError[index] != null) {
        if (groupedError[index].unit_id != null) {
          return groupedError[index].unit_id[0]?.replace(`products.${index}.unit_id`, "unit") ?? "";
        }
      }
    }

    if (field === "quantity") {
      if (groupedError[index] != null) {
        if (groupedError[index].quantity != null) {
          return groupedError[index].quantity[0]?.replace(`products.${index}.quantity`, "quantity") ?? "";
        }
      }
    }

    if (field === "unit_without_tax") {
      if (groupedError[index] != null) {
        if (groupedError[index].unit_without_tax != null) {
          return groupedError[index].unit_without_tax[0]?.replace(`products.${index}.unit_without_tax`, "unit price") ?? "";
        }
      }
    }

    if (field === "tax_percentage") {
      if (groupedError[index] != null) {
        if (groupedError[index].tax_percentage != null) {
          return groupedError[index].tax_percentage[0]?.replace(`products.${index}.tax_percentage`, "tax percentage") ?? "";
        }
      }
    }

    return "";
  };

  const calculateTotalAmount = (row) => {
    const toNumber = (v) => {
      if (v === null || v === undefined) return 0;
      const cleaned = String(v).replace(/,/g, "").trim();
      const n = parseFloat(cleaned);
      return Number.isFinite(n) ? n : 0;
    };

    const unitPrice = toNumber(row.unit_without_tax);
    const quantity = toNumber(row.quantity);
    const cgst = toNumber(row.cgst_percentage ?? 0);
    const sgst = toNumber(row.sgst_percentage ?? 0);
    const igst = toNumber(row.igst_percentage ?? 0);

    const baseAmount = unitPrice * quantity;
    const taxAmount = baseAmount * ((cgst + sgst + igst) / 100);
    const total = baseAmount + taxAmount;

    return total > 0 ? total.toFixed(2) : "0.00";
  };

  const [isIgst, setIsIgst] = useState(false);
  const [isCgst, setIsCgst] = useState(false);
  const [isSgst, setIsSgst] = useState(false);
  const getAddress = async (id) => {
    try {
      const response = await getVendorAddress(id);
      if (response.data.success === true) {
        setIsIgst(response.data.data.igst ?? false);
        setIsCgst(response.data.data.cgst ?? false);
        setIsSgst(response.data.data.sgst ?? false);
      }
    } catch (e) {}
  };
  const handleCategorySave = async () => {
    try {
      setIsCatLoader(true);

      setErrorCatMsg({});
      const payload = { name: categoryName };

      const response = await submitCategoryApi(payload);
      if (response.status === 400) {
        new Promise((resolve, reject) => {
          const validationTemp = {
            name: "",
          };
          Object.entries(response.data.error).forEach(([key, value]) => {
            console.log(`${key}: ${value}`);
            validationTemp[key] = value.length === 0 ? "" : value[0];
          });
          console.log("validationTemp...", validationTemp);
          setErrorCatMsg(validationTemp);
        });
      } else if (response.data.success === true) {
        setSnackbarMessage("Category created successfully!");
        setSnackbarStatus(true);
        handleSnackbarShow();
        setCreateCategoryModal(false);
        setSelectedCategory({ value: categoryName, label: categoryName });
        setCategoryName("");

        setErrorMsg({});
      } else {
        setErrorMsg(response.data.error || {});
        console.log(errorMsg);
        setSnackbarMessage(response.data.message || "Failed to create category.");
        setSnackbarStatus(false);
        handleSnackbarShow();
      }
    } catch (error) {
      console.error(error);
      setSnackbarMessage("Error creating category.");
      setSnackbarStatus(false);
      handleSnackbarShow();
    } finally {
      setIsCatLoader(false);
    }
  };

  const toNumber = (v) => {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : 0;
  };

  const calculateSummary = useCallback(() => {
    let subTotal = 0;
    let cgstTotal = 0;
    let sgstTotal = 0;
    let igstTotal = 0;

    productList.forEach((row) => {
      const qty = toNumber(row.quantity);
      const unit = toNumber(row.unit_without_tax);

      const base = qty * unit;
      subTotal += base;

      cgstTotal += base * (toNumber(row.cgst_percentage) / 100);
      sgstTotal += base * (toNumber(row.sgst_percentage) / 100);
      igstTotal += base * (toNumber(row.igst_percentage) / 100);
    });

    const taxTotal = cgstTotal + sgstTotal + igstTotal;
    const grossTotal = subTotal + taxTotal;

    // Round-off logic
    const roundedTotal = Math.round(grossTotal);
    const roundDiff = isRoundOff ? +(roundedTotal - grossTotal).toFixed(2) : 0;

    const finalTotal = grossTotal + roundDiff + toNumber(adjustment);

    return {
      subTotal,
      cgstTotal,
      sgstTotal,
      igstTotal,
      taxTotal,
      grossTotal,
      roundDiff,
      finalTotal,
    };
  }, [productList, isRoundOff]);

  const summary = calculateSummary();

  return (
    <>
      <ProfileImageModal show={showProfilePopup} onClose={() => setShowProfilePopup(false)} imageUrl={selectedProfile} />
      <div className="d-flex flex-column flex-xl-row">
        <Sidebar currentfile="Purchase Order" />
        <div
          className="bg-half-white"
          style={{
            width: "calc(100% - clamp(218px, 12%, 251px))",
            background: "#F5F8FA",
          }}
        >
          <Navbar pageTitle="Purchase Order" />
          <div>
            <div className="bg-white px-2 d-flex align-items-center justify-content-between">
              <div className="d-flex gap-3 my-3">
                <MoveLeft
                  className="cursor-pointer"
                  onClick={() => {
                    handleBack();
                  }}
                />
                <p className="fw-semibold m-0">New purchase order</p>
              </div>
              <div className="d-flex gap-3">
                <Button
                  className="cursor-pointer bg-white rounded-pill px-4 py-2  btn-border-grey fs-15 fw-500"
                  style={{ border: "1px solid #D4DDE7", color: "#212129" }}
                  onClick={() => {
                    handleBack();
                  }}
                >
                  Cancel
                </Button>
                <Button
                  className={`cursor-pointer dark-purple-btn ${isLoader ? "disabled-btn" : ""} rounded-pill py-2 px-4 d-flex justify-content-center align-items-center`}
                  onClick={
                    isLoader === false
                      ? () => {
                          submitForm();
                        }
                      : () => {}
                  }
                  style={{ minWidth: "100px" }}
                >
                  {isLoader ? <Spinner animation="border" size="sm" /> : id != null ? "Update" : "Save"}
                </Button>
              </div>
            </div>

            <div className="m-2 bg-white rounded-4 p-3">
              <div className="row">
                <div className="col-12 ">
                  <div className="row">
                    <div className="col-xl-12">
                      <p className="fs-18 fw-600 mb-0">Vendor Details </p>
                    </div>
                    <div className="mt-2 col-md-6 col-xl-5 col-12 p-2  ">
                      <label htmlFor="vendor" className="form-label  fw-400 my-0 d-flex me-2" style={{ width: "150px" }}>
                        Vendor <span className="error-text">*</span>
                      </label>
                      <div className="w-100 mt-2" style={{ height: 36 }}>
                        <CommonDropdown
                          apiUrl={`${apiUrl}get_vendor?term=`}
                          placeholder="Select"
                          value={vendorOption}
                          onSelect={(item) => {
                            setVendorOption(item);

                            setProductList((prev) =>
                              prev.map((item) => ({
                                ...item,
                                cgst_percentage: "",
                                igst_percentage: "",
                                sgst_percentage: "",
                              })),
                            );

                            getAddress(item.value);
                          }}
                        />

                        <span
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "start",
                            fontSize: "12px",
                            fontWeight: 400,
                            color: "#FF4444",
                            marginTop: "6px",
                            marginLeft: "6px",
                          }}
                        >
                          {errorMsg.vendor_id}
                        </span>
                      </div>
                    </div>

                    <div className="mt-2 col-md-6 col-xl-5 col-12 p-2 ">
                      <label htmlFor="vendor" className="form-label  fw-400 my-0 d-flex me-2" style={{ width: "150px" }}>
                        Project
                        {/* <span className="error-text">*</span> */}
                      </label>
                      <div className="w-100 mt-2" style={{ height: 36 }}>
                        <CommonDropdown
                          apiUrl={`${apiUrl}get_project`}
                          placeholder="Select"
                          value={projectOption}
                          onSelect={(item) => {
                            setProjectOption(item);
                          }}
                        />

                        <span
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "start",
                            fontSize: "12px",
                            fontWeight: 400,
                            color: "#FF4444",
                            marginTop: "6px",
                            marginLeft: "6px",
                          }}
                        >
                          {errorMsg.project_id}
                        </span>
                      </div>
                    </div>

                    <div className="mt-2 col-md-6 col-xl-5 col-12 p-2 ">
                      <label htmlFor="stageOption" className="form-label  fw-400 my-0 d-flex me-2" style={{ width: "150px" }}>
                        PO Number
                      </label>
                      <div className="w-100 mt-2" style={{ height: 36 }}>
                        <input type="text" className="form-control rounded-3 vendor-form-input" placeholder="Enter here" value={poNumber || ""} onChange={(e) => setPoNumber(e.target.value)} />{" "}
                        <span
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "start",
                            fontSize: "12px",
                            fontWeight: 400,
                            color: "#FF4444",
                            marginTop: "6px",
                            marginLeft: "6px",
                          }}
                        >
                          {errorMsg.po_number}
                        </span>
                      </div>
                    </div>

                    <div className="mt-2 col-md-6 col-xl-5 col-12 p-2 ">
                      <label htmlFor="description" className="form-label  fw-400 my-0 d-flex me-2" style={{ width: "150px" }}>
                        PO Date <span className="error-text">*</span>
                      </label>
                      <div className="w-100 mt-2 " style={{ height: 36 }}>
                        <CommonDatePicker selected={poDate} onChange={(date) => setPODate(date)} error={errorMsg.po_date} />
                        <span
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "start",
                            fontSize: "12px",
                            fontWeight: 400,
                            color: "#FF4444",
                            marginTop: "6px",
                            marginLeft: "6px",
                          }}
                        >
                          {errorMsg.po_date}
                        </span>
                      </div>
                    </div>

                    <div className="mt-2 col-md-6 col-xl-5 col-12 p-2 ">
                      <label htmlFor="vendor" className="form-label  fw-400 my-0 d-flex me-2" style={{ width: "150px" }}>
                        Category
                      </label>
                      <div className="w-100 d-flex align-items-center justify-content-start gap-2">
                        <div className="w-100 mt-2" style={{ height: 36 }}>
                          <CommonDropdown
                            apiUrl={`${apiUrl}get_category`}
                            placeholder="Select"
                            value={selectedCategory}
                            onSelect={(item) => {
                              setSelectedCategory(item);
                            }}
                          />
                          {/* <SearchWithFreeText
                            apiUrl={`${apiUrl}get_category`}
                            placeholder="Search & type..."
                            selectedValue={selectedCategory}
                            onDropdownChange={handleDropdownCategoryChange}
                            onChangeSearch={handleSearchCategoryChange}
                          /> */}

                          <span
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "start",
                              fontSize: "12px",
                              fontWeight: 400,
                              color: "#FF4444",
                              marginTop: "6px",
                              marginLeft: "6px",
                            }}
                          >
                            {errorMsg.category}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="mt-2 col-md-6 col-xl-5 col-12 p-2 ">
                      <label htmlFor="stageOption" className="form-label  fw-400 my-0 d-flex me-2" style={{ width: "150px" }}>
                        Currency
                      </label>
                      <div className="w-100 mt-2" style={{ height: 36 }}>
                        <CommonDropdown
                          apiUrl={`${apiUrl}get_currency`}
                          placeholder="Select"
                          value={currencyOption}
                          onSelect={(item) => {
                            setCurrencyOption({
                              ...item,
                              symbol: item.symbol ? decodeHtml(item.symbol) : "₹",
                            });
                          }}
                        />{" "}
                        <span
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "start",
                            fontSize: "12px",
                            fontWeight: 400,
                            color: "#FF4444",
                            marginTop: "6px",
                            marginLeft: "6px",
                          }}
                        >
                          {errorMsg.currency_id}
                        </span>
                      </div>
                    </div>

                    <div className="mt-2 col-md-6 col-xl-5 col-12 p-2 ">
                      <label htmlFor="stageOption" className="form-label  fw-400 my-0 d-flex me-2" style={{ width: "150px" }}>
                        Payment Type
                      </label>
                      <div className="w-100 mt-2 " style={{ height: 36 }}>
                        <CommonDropdown
                          apiUrl={`${apiUrl}get_payment_type?term=`}
                          placeholder="Select"
                          value={paymentTypeOption}
                          onSelect={(item) => {
                            setPaymentTypeOption(item);
                          }}
                        />{" "}
                        <span
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "start",
                            fontSize: "12px",
                            fontWeight: 400,
                            color: "#FF4444",
                            marginTop: "6px",
                            marginLeft: "6px",
                          }}
                        >
                          {errorMsg.payment_type}
                        </span>
                      </div>
                    </div>
                    <div className="mt-2 col-md-6 col-xl-5 col-12 p-2 ">
                      <label htmlFor="description" className="form-label  fw-400 my-0 d-flex me-2" style={{ width: "150px" }}>
                        Delivery Date
                      </label>
                      <div className="w-100 mt-2 " style={{ height: 36 }}>
                        <CommonDatePicker selected={deliveryDate} onChange={(date) => setDeliveryDate(date)} error={errorMsg.delivery_date} />
                        <span
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "start",
                            fontSize: "12px",
                            fontWeight: 400,
                            color: "#FF4444",
                            marginTop: "6px",
                            marginLeft: "6px",
                          }}
                        >
                          {errorMsg.delivery_date}
                        </span>
                      </div>
                    </div>
                    <div className="mt-2 col-md-6 col-xl-5 col-12 p-2 ">
                      <label htmlFor="description" className="form-label  fw-400 my-0 d-flex me-2" style={{ width: "150px" }}>
                        Payment Terms
                      </label>
                      <div className="w-100 mt-2 ">
                        <textarea
                          type="text"
                          className="form-control rounded-3 vendor-form-input"
                          id="paymentTerms"
                          placeholder="Enter here"
                          rows={2}
                          value={paymentTerms}
                          onChange={(e) => {
                            setPaymentTerms(e.target.value);
                          }}
                        />{" "}
                        <span
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "start",
                            fontSize: "12px",
                            fontWeight: 400,
                            color: "#FF4444",
                            marginTop: "6px",
                            marginLeft: "6px",
                          }}
                        >
                          {errorMsg.payment_terms}
                        </span>
                      </div>
                    </div>
                    <div className="mt-2 col-md-6 col-xl-5 col-12 p-2 ">
                      <label htmlFor="description" className="form-label  fw-400 my-0 d-flex me-2" style={{ width: "150px" }}>
                        Inco Terms
                      </label>
                      <div className="w-100 mt-2 ">
                        <textarea
                          type="text"
                          className="form-control rounded-3 vendor-form-input"
                          id="incoTerms"
                          placeholder="Enter here"
                          rows={2}
                          value={incoTerms}
                          onChange={(e) => {
                            setIncoTerms(e.target.value);
                          }}
                        />{" "}
                        <span
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "start",
                            fontSize: "12px",
                            fontWeight: 400,
                            color: "#FF4444",
                            marginTop: "6px",
                            marginLeft: "6px",
                          }}
                        >
                          {errorMsg.inco_terms}
                        </span>
                      </div>
                    </div>
                    <div className="mt-2 col-md-6 col-xl-5 col-12 p-2 ">
                      <label htmlFor="description" className="form-label  fw-400 my-0 d-flex me-2" style={{ width: "150px" }}>
                        Note & Instructions
                      </label>
                      <div className="w-100 mt-2 ">
                        <textarea
                          type="text"
                          className="form-control rounded-3 vendor-form-input"
                          id="noteIntrunction"
                          placeholder="Enter here"
                          rows={2}
                          value={noteInstructions}
                          onChange={(e) => {
                            setNoteInstructions(e.target.value);
                          }}
                        />{" "}
                        <span
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "start",
                            fontSize: "12px",
                            fontWeight: 400,
                            color: "#FF4444",
                            marginTop: "6px",
                            marginLeft: "6px",
                          }}
                        >
                          {errorMsg.note_instruction}
                        </span>
                      </div>
                    </div>

                    <div className="mt-2 col-md-6 col-xl-5 col-12 p-2 ">
                      <label htmlFor="description" className="form-label  fw-400 my-0 d-flex me-2" style={{ width: "150px" }}>
                        Term & Conditions
                      </label>
                      <div className="w-100 mt-2 ">
                        <textarea
                          type="text"
                          className="form-control rounded-3 vendor-form-input"
                          id="termConditions"
                          placeholder="Enter here"
                          rows={2}
                          value={termConditions}
                          onChange={(e) => {
                            setTermConditions(e.target.value);
                          }}
                        />{" "}
                        <span
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "start",
                            fontSize: "12px",
                            fontWeight: 400,
                            color: "#FF4444",
                            marginTop: "6px",
                            marginLeft: "6px",
                          }}
                        >
                          {errorMsg.note_instruction}
                        </span>
                      </div>
                    </div>
                    <div className="mt-2 col-md-6 col-xl-5 col-12 p-2 ">
                      <label htmlFor="description" className="form-label  fw-400 my-0 d-flex me-2" style={{ width: "150px" }}>
                        Description
                      </label>
                      <div className="w-100 mt-2 " style={{ height: 36 }}>
                        <textarea
                          type="text"
                          className="form-control rounded-3 vendor-form-input"
                          id="description"
                          placeholder="Enter here"
                          rows={2}
                          value={description}
                          onChange={(e) => {
                            setDescription(e.target.value);
                          }}
                        />{" "}
                        <span
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "start",
                            fontSize: "12px",
                            fontWeight: 400,
                            color: "#FF4444",
                            marginTop: "6px",
                            marginLeft: "6px",
                          }}
                        >
                          {errorMsg.description}
                        </span>
                      </div>
                    </div>
                    <div className="mt-2 col-md-6 col-xl-5 col-12 p-2 ">
                      <label htmlFor="description" className="form-label  fw-400 my-0 d-flex me-2" style={{ width: "150px" }}>
                        Authorized Signature
                      </label>
                      <div className="w-100 mt-2 ">
                        {uploadedFiles.length > 0 && (
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "row",
                              gap: "12px",
                              marginBottom: "16px",
                              overflowX: "auto",
                              paddingBottom: "8px",
                            }}
                          >
                            {uploadedFiles.map((file) => (
                              <div
                                key={file.id}
                                style={{
                                  position: "relative",
                                  width: "250px",
                                  height: "60px",
                                  borderRadius: "12px",
                                  overflow: "hidden",
                                  backgroundColor: "#F3F4F6",
                                  cursor: "pointer",
                                  flexShrink: 0,
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openFile(file.preview, file.name ?? "");
                                }}
                              >
                                {/* Image Preview */}
                                {file.preview ? (
                                  <img
                                    src={file.preview}
                                    alt={file.name ?? ""}
                                    style={{
                                      width: "100%",
                                      height: "60px",
                                      objectFit: "fit",
                                    }}
                                  />
                                ) : (
                                  // Fallback (non-image)
                                  <div
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      color: "#9CA3AF",
                                      fontSize: "14px",
                                    }}
                                  >
                                    No Preview
                                  </div>
                                )}

                                {/* Remove Button */}
                                <FaTimes
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setUploadedFiles([]);
                                  }}
                                  style={{
                                    position: "absolute",
                                    top: "8px",
                                    right: "8px",
                                    fontSize: "18px",
                                    color: "#fff",
                                    background: "rgba(0,0,0,0.6)",
                                    borderRadius: "50%",
                                    padding: "4px",
                                    cursor: "pointer",
                                  }}
                                />
                              </div>
                            ))}
                          </div>
                        )}
                        {viewFiles.length > 0 && (
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "row",
                              gap: "12px",
                              marginBottom: "16px",
                              overflowX: "auto",
                              paddingBottom: "8px",
                            }}
                          >
                            {viewFiles.map((file) => (
                              <div
                                key={file.id}
                                style={{
                                  position: "relative",
                                  width: "250px",
                                  height: "60px",
                                  borderRadius: "12px",
                                  overflow: "hidden",
                                  backgroundColor: "#F3F4F6",
                                  cursor: "pointer",
                                  flexShrink: 0,
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openFile(file.preview, file.name ?? "");
                                }}
                              >
                                {/* Image Preview */}
                                {file.preview ? (
                                  <img
                                    src={file.preview}
                                    alt={file.name ?? ""}
                                    style={{
                                      width: "100%",
                                      height: "60px",
                                      objectFit: "fit",
                                    }}
                                  />
                                ) : (
                                  // Fallback (non-image)
                                  <div
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      color: "#9CA3AF",
                                      fontSize: "14px",
                                    }}
                                  >
                                    No Preview
                                  </div>
                                )}

                                {/* Remove Button */}
                                <FaTimes
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setViewFiles([]);
                                    setImageRemoved(true);
                                  }}
                                  style={{
                                    position: "absolute",
                                    top: "8px",
                                    right: "8px",
                                    fontSize: "18px",
                                    color: "#fff",
                                    background: "rgba(0,0,0,0.6)",
                                    borderRadius: "50%",
                                    padding: "4px",
                                    cursor: "pointer",
                                  }}
                                />
                              </div>
                            ))}
                          </div>
                        )}

                        {uploadedFiles.length === 0 && viewFiles.length === 0 && (
                          <div
                            onClick={handleSelectFile}
                            style={{
                              border: "2px dashed #ADC5FF",
                              borderRadius: "8px",
                              padding: "12px",
                              textAlign: "center",
                              cursor: "pointer",
                              transition: "all 0.2s",
                              backgroundColor: "transparent",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: "12px",
                            }}
                          >
                            <div
                              style={{
                                width: "40px",
                                height: "40px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                              }}
                            >
                              <img src={fileUploadIcon} alt="Upload" width="40" height="40" />
                            </div>
                            <div style={{ textAlign: "left" }}>
                              <div className="fs-13 fw-400" style={{ color: "#374151", marginBottom: "2px" }}>
                                Drag and drop or{" "}
                                <span
                                  style={{
                                    color: "#5B50FE",
                                    cursor: "pointer",
                                    fontWeight: "500",
                                  }}
                                >
                                  Select a sign image
                                </span>
                              </div>
                              <div className="fs-12" style={{ color: "#9CA3AF" }}>
                                You can add image.
                              </div>
                            </div>
                          </div>
                        )}
                        <span
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "start",
                            fontSize: "12px",
                            fontWeight: 400,
                            color: "#FF4444",
                            marginTop: "6px",
                            marginLeft: "6px",
                          }}
                        >
                          {errorMsg.note_instruction}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="m-2 bg-white rounded-4 p-3">
              <div className="row">
                <div className="col-12 ">
                  <div className="row">
                    <div className="col-xl-12 d-flex align-items-center justify-content-between">
                      <p className="fs-18 fw-600 mb-0">Product</p>

                      <label className="d-flex align-items-center gap-2 cursor-pointer">
                        <span className="fs-14 fw-500">Round-off</span>
                        <input type="checkbox" className="form-check-input m-0" checked={isRoundOff} onChange={(e) => setIsRoundOff(e.target.checked)} />
                      </label>
                    </div>

                    <div style={{ overflowX: "auto" }}>
                      <div
                        className="bg-white rounded-4 mt-3"
                        style={{
                          minWidth: "100%",
                        }}
                      >
                        {/* Header Row */}
                        <div
                          style={{
                            backgroundColor: "#F2F2F2",
                            display: "grid",
                            gridTemplateColumns: (() => {
                              const hasException = productList.some((_, idx) => getErrorMsg(idx, "exception"));
                              let cols = [];
                              if (hasException) cols.push("80px");
                              cols.push("minmax(150px, 1fr)", "100px", "110px", "140px", "80px", "100px");
                              if (isCgst) cols.push("75px");
                              if (isSgst) cols.push("75px");
                              if (isIgst) cols.push("75px");
                              cols.push("110px", "50px");
                              return cols.join(" ");
                            })(),
                            padding: "10px 16px",
                            gap: "8px",
                            alignItems: "center",
                            borderBottom: "1px solid #EBEBEB",
                            width: "100%",
                          }}
                        >
                          {productList.some((_, index) => getErrorMsg(index, "exception")) && <div className="fs-13 fw-700">Proceed</div>}
                          <div className="fs-13 fw-700">Product Name</div>
                          <div className="fs-13 fw-700">Product ID</div>
                          <div className="fs-13 fw-700">Unit</div>
                          <div className="fs-13 fw-700">Expected Date</div>
                          <div className="fs-13 fw-700">Quantity</div>
                          <div className="fs-13 fw-700">Unit Price</div>
                          {isCgst && <div className="fs-13 fw-700">CGST %</div>}
                          {isSgst && <div className="fs-13 fw-700">SGST %</div>}
                          {isIgst && <div className="fs-13 fw-700">IGST %</div>}
                          <div className="fs-13 fw-700">Total Amount</div>
                          {/* <div className="fs-13 fw-700">TDS %</div> */}
                          <div className="fs-13 fw-700"></div>
                        </div>

                        {productList.map((row, index) => {
                          const quantityError = getErrorMsg(index, "exception");
                          return (
                            <div
                              key={index}
                              style={{
                                borderBottom: "1px solid #EBEBEB",
                                paddingBottom: "6px",
                              }}
                            >
                              <div
                                key={index}
                                style={{
                                  display: "grid",
                                  gridTemplateColumns: (() => {
                                    const hasException = productList.some((_, idx) => getErrorMsg(idx, "exception"));
                                    let cols = [];
                                    if (hasException) cols.push("80px");
                                    cols.push("minmax(150px, 1fr)", "100px", "110px", "140px", "80px", "100px");
                                    if (isCgst) cols.push("75px");
                                    if (isSgst) cols.push("75px");
                                    if (isIgst) cols.push("75px");
                                    cols.push("110px", "50px");
                                    return cols.join(" ");
                                  })(),
                                  padding: "10px 16px",
                                  gap: "8px",
                                  alignItems: "center",
                                }}
                              >
                                {/* Exception/Proceed Column */}
                                {productList.some((_, idx) => getErrorMsg(idx, "exception")) && (
                                  <div>
                                    {quantityError && (
                                      <input
                                        type="checkbox"
                                        checked={row.proceed ?? false}
                                        onChange={(e) => {
                                          const productTemp = [...productList];
                                          productTemp[index].proceed = e.target.checked;
                                          setProductList(productTemp);
                                        }}
                                        style={{ cursor: "pointer" }}
                                      />
                                    )}
                                  </div>
                                )}

                                {/* Product Name Dropdown */}
                                <CommonTooltip text={row.product?.label} id={`tooltip-product-${index}`}>
                                  <CommonDropdown
                                    apiUrl={`${apiUrl}get_product?term=`}
                                    placeholder="Select"
                                    value={row.product}
                                    onSelect={(item) => {
                                      const productTemp = [...productList];
                                      productTemp[index].product = item;
                                      setProductList(productTemp);
                                      getProductDetails(item.value, index);
                                    }}
                                  />
                                  <span className="fs-12 fw-400 text-danger">{getErrorMsg(index, "product_id")}</span>
                                </CommonTooltip>

                                {/* Product ID */}
                                <CommonTooltip text={row.product_code} id={`tooltip-code-${index}`} className="form-control bg-light" style={{ minHeight: "38px" }} ellipsis={true} />

                                {/* Unit Dropdown */}
                                <CommonTooltip text={row.unit?.label} id={`tooltip-unit-${index}`}>
                                  <CommonDropdown
                                    apiUrl={`${apiUrl}get_uom?term=`}
                                    placeholder="Select"
                                    value={row.unit}
                                    onSelect={(item) => {
                                      const productTemp = [...productList];
                                      productTemp[index].unit = item;
                                      setProductList(productTemp);
                                    }}
                                  />
                                  <span className="fs-12 fw-400 text-danger">{getErrorMsg(index, "unit_id")}</span>
                                </CommonTooltip>

                                <CommonDatePicker
                                  selected={row.date}
                                  onChange={(date) => {
                                    const productTemp = [...productList];
                                    productTemp[index].date = date;
                                    setProductList(productTemp);
                                  }}
                                />

                                {/* Rest of the input fields */}
                                <div className="d-flex flex-column">
                                  <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Quantity"
                                    value={row.quantity}
                                    onChange={(e) => {
                                      const productTemp = [...productList];
                                      productTemp[index].quantity = e.target.value.replace(/[^0-9.]/g, "");
                                      setProductList(productTemp);
                                    }}
                                  />
                                  <span className="fs-12 fw-400 text-danger">{getErrorMsg(index, "quantity")}</span>
                                </div>

                                <div className="d-flex flex-column">
                                  <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Unit price"
                                    value={row.unit_without_tax}
                                    onChange={(e) => {
                                      const productTemp = [...productList];
                                      productTemp[index].unit_without_tax = e.target.value.replace(/[^0-9.]/g, "");
                                      setProductList(productTemp);
                                    }}
                                  />
                                  <span className="fs-12 fw-400 text-danger">{getErrorMsg(index, "unit_without_tax")}</span>
                                </div>

                                {isCgst && (
                                  <input
                                    type="text"
                                    className="form-control"
                                    placeholder="CGST %"
                                    value={row.cgst_percentage}
                                    onChange={(e) => {
                                      const productTemp = [...productList];
                                      productTemp[index].cgst_percentage = e.target.value.replace(/[^0-9.]/g, "");
                                      setProductList(productTemp);
                                    }}
                                  />
                                )}

                                {isSgst && (
                                  <input
                                    type="text"
                                    className="form-control"
                                    placeholder="SGST %"
                                    value={row.sgst_percentage}
                                    onChange={(e) => {
                                      const productTemp = [...productList];
                                      productTemp[index].sgst_percentage = e.target.value.replace(/[^0-9.]/g, "");
                                      setProductList(productTemp);
                                    }}
                                  />
                                )}

                                {isIgst && (
                                  <input
                                    type="text"
                                    className="form-control"
                                    placeholder="IGST %"
                                    value={row.igst_percentage}
                                    onChange={(e) => {
                                      const productTemp = [...productList];
                                      productTemp[index].igst_percentage = e.target.value.replace(/[^0-9.]/g, "");
                                      setProductList(productTemp);
                                    }}
                                  />
                                )}

                                {/* Total Amount */}
                                <div className="form-control bg-light">
                                  {currencyOption?.symbol || "₹"} {calculateTotalAmount(row)}
                                </div>

                                {/* TDS */}
                                {/* <input
                              type="text"
                              className="form-control"
                              placeholder="TDS %"
                              value={row.tds}
                              onChange={(e) => {
                                const productTemp = [...productList];
                                productTemp[index].tds = e.target.value.replace(/[^0-9.]/g, "");
                                setProductList(productTemp);
                              }}
                            /> */}

                                {/* Delete Button */}
                                <button
                                  className="btn btn-link p-0"
                                  onClick={() => {
                                    if (row.id) {
                                      setRemovedProductList([...removedProductList, row.id]);
                                    }
                                    const productTemp = [...productList];
                                    productTemp.splice(index, 1);
                                    setProductList(productTemp);
                                  }}
                                >
                                  <img src={deleteIcon} alt="delete" />
                                </button>

                                {/* Error Message */}
                              </div>
                              {quantityError && (
                                <div className="fs-12 fw-400 text-danger grid-column-full">
                                  <InfoIc width={12} height={12} /> {quantityError}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    <div className="col-xl-12 mt-2">
                      <Button
                        className={`cursor-pointer border-0 rounded-pill py-2 px-4 d-flex justify-content-center align-items-center`}
                        onClick={() => {
                          setProductList([
                            ...productList,
                            {
                              id: 0,
                              proceed: null,
                              product: null,
                              product_code: null,
                              product_img: null,
                              unit: null,
                              date: null,
                              quantity: 0,
                              unit_without_tax: 0,
                              tax_percentage: 0,
                            },
                          ]);
                        }}
                        style={{
                          minWidth: "100px",
                          background: "#E6E4FF",
                          color: "#212129",
                          fontWeight: "600",
                          fontSize: "13px",
                        }}
                      >
                        Add product
                      </Button>
                    </div>
                  </div>
                  <AmountSummary 
                    subTotal={summary.subTotal} 
                    cgst={summary.cgstTotal} 
                    sgst={summary.sgstTotal} 
                    igst={summary.igstTotal} 
                    roundOff={summary.roundDiff} 
                    showRoundOff={isRoundOff} 
                    adjustment={adjustment} 
                    setAdjustment={setAdjustment} 
                    currencySymbol={currencyOption?.symbol || "₹"}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {createCategoryModal && (
        <>
          {/* Backdrop */}
          <div className="modal-backdrop fade show" style={{ zIndex: 1040 }}></div>

          {/* Modal */}
          <div className="modal show d-block" tabIndex="-1" style={{ zIndex: 1050 }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content rounded-4 p-3">
                <div className="modal-header border-0">
                  <h5 className="modal-title fs-20 fw-700">New Category</h5>
                  <button
                    type="button"
                    className="btn-close border rounded-circle"
                    onClick={() => {
                      setCreateCategoryModal(false);
                      setErrorMsg({});

                      setCategoryName("");
                    }}
                  ></button>
                </div>

                {/* Body */}
                <div className="modal-body">
                  <form>
                    <div className="mb-3">
                      <label className="form-label">Category Name</label>
                      <input type="text" className="form-control" placeholder="Enter Name" value={categoryName} onChange={(e) => setCategoryName(e.target.value)} />
                      <div>
                        {errorCatMsg.name && (
                          <span className="text-danger" style={{ fontSize: 12 }}>
                            {errorCatMsg.name}
                          </span>
                        )}
                      </div>
                    </div>
                  </form>
                </div>

                {/* Footer */}
                <div className="modal-footer gap-3 border-0">
                  <button
                    className="btn bg-transparent border px-3"
                    type="button"
                    onClick={() => {
                      setCreateCategoryModal(false);
                      setErrorMsg({});
                      setCategoryName("");
                    }}
                  >
                    Cancel
                  </button>
                  <button className="btn text-white px-4" type="button" style={{ backgroundColor: "#4F46E5", width: "90px" }} onClick={handleCategorySave} disabled={isCatLoader} w>
                    {isCatLoader ? <Spinner animation="border" size="sm" /> : "Save"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};
